/** @jsxImportSource @emotion/react */
import 'react-datepicker/dist/react-datepicker.css';
import * as React from 'react';
import DatePicker,  { registerLocale, setDefaultLocale } from 'react-datepicker';
import { SerializedStyles, ClassNames, css } from '@emotion/react';
import { InputEffects, InputSize, InputShape, InputColors } from './Input';
import { connect } from 'react-redux';
import { IAppState } from '../../core/redux/IAppState';
import { assoc } from 'ramda';
import { getResource } from '../../language/resources/getResource';
import * as loc from 'date-fns/locale';

/**
 * First entry should match the LOCALE_KEY defined in the language resource files
 */
registerLocale('en_us', loc.enUS);
registerLocale('French', loc.frCA);
registerLocale('es_es', loc.es);
setDefaultLocale('en_us');

const base = css`
  ${InputEffects}
  ${InputSize}
  ${InputShape}
  ${InputColors}
  width: 10.3rem;

  ::-ms-clear {
    display: none;
  }`;


type Props = {
  id?: string;
  date?: Date;
  disabled?: boolean;
  onChange: (date: Date | null) => void;
  placeholderText?: string;
  tooltipText?: string;
  rootCss?: SerializedStyles;
  locale?: string;
  dateFormat?: string;
  minDate?: Date;
  maxDate?: Date;
  dayCss?: (date: Date) => string;
  excludeDates?: Date[] | {date: Date, message: string}[]
}


/**
 * DateInput: a component that provides a text input with accompanying datepicker widget that enables both easy keyboard
 * input and mouse selection. Custom styling can be passed in via the `className` parameter.
 *
 * PureComponent: true
 * Connected to Redux: true
 */
export default connect(
  (state: IAppState, props: Props) => ({
    locale: getResource(state.locale, 'LOCALE_KEY'),
    dateFormat: state.defaultDateFormat,
  }),
)(class DateInput extends React.PureComponent<Props>{

  public componentDidUpdate(prevProps: Props){
    if (prevProps.locale !== this.props.locale) {
      this.setState(prev => assoc('locale', this.props.locale, prev));
    }
  }

  public dayClassName = this.props.dayCss ? (date: Date) => (this.props.dayCss!(date)) : undefined

  public render = () => (
    <ClassNames>
     {() => {
        return (
          <DatePicker
            id={this.props.id}
            placeholderText={this.props.placeholderText}
            selected={this.props.date}
            locale={this.props.locale ?? 'en_us'}
            onChange={this.props.onChange}
            title={this.props.tooltipText}
            readOnly={this.props.disabled}
            dateFormat={this.props.dateFormat}
            disabled={this.props.disabled}
            minDate={this.props.minDate}
            maxDate={this.props.maxDate}
            dayClassName={this.dayClassName}
            excludeDates={this.props.excludeDates}
            css={css`${base} ${this.props.rootCss}`}
            autoComplete="off"
          />
        );
      }}
    </ClassNames>
    );
  },
);

